import { generateMnemonic, mnemonicToEntropy, validateMnemonic } from "bip39";
import { sha256 } from "js-sha256";
import { derivePath } from "ed25519-hd-key";
import { Keypair } from "@solana/web3.js";
import { Buffer } from "buffer";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function uint8ArraysEqual(a, b) {
  if (a === b) return true;
  if (a.byteLength !== b.byteLength) return false;

  for (let i = 0; i < a.byteLength; i++) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}

export async function deriveKey(password, salt = "unlink-salt") {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode(salt),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function deriveUserId(password) { 
  const enc = new TextEncoder();
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", enc.encode(password));
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32); // Optional: truncate to 32 hex chars
}

export async function deriveKeyFromPassword(password, salt) {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return await window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: 250000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

// TEMP: Fake unlink secret (simulating encrypted blob)
export const unlinkSecret = "U2FsdGVkX1+FakeEncryptedSecretExample123==";

// TEMP: Fake decrypt function for demo/testing
export async function decryptSecret(encryptedSecret, password) { alert('cryptohelpers doet nog niks');
  // Simulated delay
  await new Promise((res) => setTimeout(res, 300));

  if (password === "test123") {
    return "fake-unlink-secret-for-demo";
  } else {
    throw new Error("Wrong password");
  }
}

/**
 * Generate a new unlink secret and its corresponding view key.
 * @returns {{ unlinkSecret: string, viewKey: string }}
 */
export function generateSecret() {
  const mnemonic = generateMnemonic(128); // 12-word mnemonic
  const entropy = mnemonicToEntropy(String(mnemonic).trim());
  const unlinkSecret = mnemonic;
  const viewKey = Buffer.from(sha256(entropy)).toString("base64").slice(0, 44);

  return { unlinkSecret, viewKey };
}


/**
 * Derives a view key from the given unlink secret (mnemonic).
 * @param {string} unlinkSecret - The 12-word mnemonic.
 * @returns {string|null} viewKey - A 44-character deterministic key, or null if invalid.
 */
export function deriveViewKey(unlinkSecret) {
  const mnemonic = String(unlinkSecret).trim().toLowerCase();

  if (!validateMnemonic(mnemonic)) {
    console.error("Invalid mnemonic");
    return null;
  }

  const entropy = mnemonicToEntropy(mnemonic);
  const viewKey = Buffer.from(sha256(entropy)).toString("base64").slice(0, 44);
  return viewKey;
}

export async function decryptData(payload, key) {
  if (!payload || payload.format !== "unlink-v1") {
    throw new Error("Invalid format");
  }

  const iv = new Uint8Array(atob(payload.iv).split("").map(c => c.charCodeAt(0)));
  const encrypted = new Uint8Array(atob(payload.data).split("").map(c => c.charCodeAt(0)));

  const decrypted = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    encrypted
  );

  return new TextDecoder().decode(decrypted);
}

export async function encryptData(data, key) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(data);

  const encrypted = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );

  return {
    iv: btoa(String.fromCharCode(...iv)),
    data: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
    format: "unlink-v1"
  };
}

export async function encryptMessage({ message, password, salt }) {
  if (!(salt instanceof Uint8Array)) {
    throw new TypeError("encryptMessage: salt must be a Uint8Array");
  }
  if (typeof message !== "string") {
    throw new TypeError("encryptMessage: message must be a JSON string");
  }

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoder.encode(message)
  );

  return {
    encryptedContent: btoa(
      JSON.stringify({
        iv: Array.from(iv),
        data: Array.from(new Uint8Array(ciphertext)),
      })
    ),
  };
}

export async function decryptMessage(encryptedContent, password, salt) {
  if (!(salt instanceof Uint8Array)) {
    throw new TypeError("decryptMessage: salt must be a Uint8Array");
  }

  try {
    const parsed = atob(encryptedContent);
    const { iv, data } = JSON.parse(parsed);

    if (!Array.isArray(iv) || iv.length !== 12) {
      throw new Error("Invalid IV format in encrypted payload");
    }

    const key = await deriveKey(password, salt);

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(iv) },
      key,
      new Uint8Array(data)
    );

    return decoder.decode(decrypted);
  } catch (err) {
    console.error("decryptMessage error:", err);
    throw err;
  }
}